import * as pdfjsLib from 'pdfjs-dist';
import { DEFAULT_FONT_FAMILY, fontCss } from '../constants';
import { getFamily } from './fonts';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

/**
 * pdf.js side of the editor: document loading, page rasterising and text-layer
 * extraction. Everything returns coordinates in *view space* — the coordinate
 * system of `page.getViewport({ scale: 1 })`, i.e. the page already turned by
 * its own `/Rotate`, origin top-left, y growing downwards.
 *
 * Objects the user draws are stored in that same space, which is why rotating a
 * page in the editor never has to re-project them: the extra rotation is a CSS
 * transform on screen and an added `/Rotate` on export, so annotations turn
 * together with the content underneath them.
 */

/** Load a File/Blob/ArrayBuffer into a pdf.js document. */
export async function loadPdfJsDoc(source) {
    const buffer = source instanceof ArrayBuffer ? source : await source.arrayBuffer();
    // pdf.js transfers (and detaches) the buffer it is given — hand it a copy so
    // the caller's ArrayBuffer stays usable for pdf-lib on export.
    const data = new Uint8Array(buffer.slice(0));
    try {
        return await pdfjsLib.getDocument({
            data,
            isEvalSupported: false,
            // Keeps `font.data` (the font program pdf.js built) on the main
            // thread instead of nulling it after the face is bound — the export
            // re-embeds it so edited paragraphs stay real text in the same
            // typeface rather than a raster stamp.
            fontExtraProperties: true,
        }).promise;
    } catch (err) {
        const message = String(err?.message || '').toLowerCase();
        if (message.includes('password')) throw new Error('password');
        throw err;
    }
}

/**
 * Geometry for every page of the document, in view space.
 * @returns {Promise<Array<{ pageNumber: number, width: number, height: number, rotation: number }>>}
 */
export async function readPageGeometry(pdf) {
    const out = [];
    for (let n = 1; n <= pdf.numPages; n += 1) {
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(n);
        const viewport = page.getViewport({ scale: 1 });
        out.push({
            pageNumber: n,
            width: viewport.width,
            height: viewport.height,
            rotation: ((page.rotate % 360) + 360) % 360,
        });
    }
    return out;
}

/**
 * One render at a time per page.
 *
 * pdf.js caches page proxies, so the viewport and the thumbnail strip ask the
 * *same* proxy to render; two overlapping `render()` calls on one proxy never
 * settle, leaving pages blank at random. Chaining them per page keeps both
 * consumers working without either knowing about the other.
 */
const renderQueues = new WeakMap();

function queueRender(pdf, pageNumber, task) {
    if (!renderQueues.has(pdf)) renderQueues.set(pdf, new Map());
    const queue = renderQueues.get(pdf);
    const next = (queue.get(pageNumber) || Promise.resolve())
        .catch(() => {})
        .then(task);
    queue.set(pageNumber, next.catch(() => {}));
    return next;
}

/** Render one page into a fresh canvas at `scale`. */
export function renderPageCanvas(pdf, pageNumber, scale, signal) {
    return queueRender(pdf, pageNumber, () => renderPageCanvasNow(pdf, pageNumber, scale, signal));
}

async function renderPageCanvasNow(pdf, pageNumber, scale, signal) {
    if (signal?.aborted) throw new Error('cancelled');
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const ctx = canvas.getContext('2d', { alpha: false });
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const task = page.render({ canvasContext: ctx, viewport, background: '#ffffff' });
    signal?.addEventListener?.('abort', () => task.cancel(), { once: true });
    await task.promise;
    return canvas;
}

/**
 * Render one view-space rect of a page at high resolution.
 *
 * Colour sampling needs this: on screen a 7pt glyph is almost entirely
 * anti-aliasing, so its darkest pixel is mid-grey and black ink reads back as
 * grey. Rendering just the line at 4× gives real stroke cores without paying
 * for a full-page raster.
 */
export function renderRegionCanvas(pdf, pageNumber, rect, scale = 4) {
    return queueRender(pdf, pageNumber, async () => {
        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.ceil(rect.w * scale));
        canvas.height = Math.max(1, Math.ceil(rect.h * scale));
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({
            canvasContext: ctx,
            viewport,
            background: '#ffffff',
            transform: [1, 0, 0, 1, -rect.x * scale, -rect.y * scale],
        }).promise;
        return canvas;
    });
}

/** Render a page to a JPEG data URL (thumbnails). */
export async function renderPageThumb(pdf, pageNumber, thumbWidth) {
    const page = await pdf.getPage(pageNumber);
    const base = page.getViewport({ scale: 1 });
    const scale = thumbWidth / base.width;
    const canvas = await renderPageCanvas(pdf, pageNumber, scale);
    return canvas.toDataURL('image/jpeg', 0.7);
}

const LINE_TOLERANCE = 0.6;

/**
 * pdf.js font objects per font id, carrying the real name
 * (`ABCDEF+TimesNewRoman,BoldItalic`) *and* the `@font-face` family pdf.js
 * loaded the embedded font program under.
 *
 * `getTextContent()` only reports the opaque id; the descriptor appears in
 * `commonObjs` once the page has been *rendered*, which the editor does anyway
 * to show it. Read it opportunistically and fall back to the CSS family the
 * text content already carries.
 *
 * Do NOT force this with `getOperatorList()`: issuing an oplist request while a
 * display render is in flight on the same page proxy starves that render, and
 * the page never rasterises at all.
 */
function readFontDescriptors(page, fontIds) {
    const map = new Map();
    for (const id of fontIds) {
        try {
            if (page.commonObjs.has(id)) {
                const font = page.commonObjs.get(id);
                if (font) map.set(id, font);
            }
        } catch {
            // font not resolved for this id — fall back to the CSS family
        }
    }
    return map;
}

const SERIF_NAMES = /times|georgia|garamond|bookman|book\b|roman|minion|cambria|palatino|utopia|charter|georgia|nimbusrom|serif/i;
const MONO_NAMES = /mono|courier|consol/i;
const BENGALI = /[\u0980-\u09FF]/;

/**
 * Families the catalogue does not carry, mapped to a *metric-compatible* one.
 *
 * These are the Microsoft Office defaults, which no web catalogue may ship \u2014
 * Google publishes clones with identical advance widths instead, so a document
 * set in Calibri comes back with lines that break in the same places.
 */
const METRIC_CLONES = {
    calibri: 'Carlito',
    cambria: 'Caladea',
    'segoe ui': 'Open Sans',
};

/** The catalogue family for a PDF font's own name, if there is a real match. */
function catalogueFamilyFor(names) {
    // The spaced spelling first: "TrebuchetMS" is only a family as "Trebuchet MS".
    for (const name of [...names].reverse()) {
        const match = getFamily(name);
        if (match) return match.name;
        const clone = METRIC_CLONES[name.toLowerCase()];
        if (clone) return clone;
    }
    return null;
}

/** `ABCDEF+Calibri-BoldItalic` → `Calibri`, plus a spaced `Trebuchet MS` variant. */
function familyNamesOf(rawName) {
    const base = String(rawName || '')
        .replace(/^[A-Z]{6}\+/, '')
        .split(/[,;]/)[0]
        .replace(/[-_](regular|book|roman|medium|light|bold|black|heavy|semibold|demibold|italic|oblique|boldital(ic)?|bolditalic|boldoblique|it|bd|mt|ps|psmt)+$/i, '')
        .replace(/(PSMT|MT|PS)$/i, '')
        .trim();
    if (!base || base.length < 3 || /[^A-Za-z0-9 .-]/.test(base)) return [];
    // "TrebuchetMS" is a real family only when it is spelled "Trebuchet MS".
    const spaced = base.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim();
    return spaced && spaced !== base ? [base, spaced] : [base];
}

/**
 * Editor font family, weight/slant and — when the document embeds the font — the
 * document's own face.
 *
 * pdf.js registers every embedded font program as a real `@font-face` named
 * after the font's `loadedName` and paints the page with it, so the canvas can
 * ask for that same family and reproduce the document's typeface exactly
 * instead of a lookalike from the catalogue. `embedded.fontStack` is built the
 * way pdf.js builds its own `ctx.font` (`systemFontInfo.css` for a font it
 * substituted from the system), then extended with the family name from the PDF
 * and the catalogue CSS: the face lives only as long as the pdf.js document,
 * and a subset carries glyphs only for the characters the document already
 * used, so anything it cannot draw still has to land somewhere sensible.
 *
 * Weight and slant are read twice, because they mean different things per
 * family. `Georgia-Italic` embeds *already slanted* glyphs and pdf.js paints it
 * upright (its `italic` flag stays unset) — asking for CSS italic on top would
 * slant it twice. The catalogue family has no such glyphs, so there the name
 * has to be believed and the slant synthesised.
 *
 * The name is tested with `sans-serif` stripped out first — otherwise every
 * sans font matches the `serif` pattern hiding inside it and body text comes
 * back set in Times.
 *
 * @param {object|string} font pdf.js font object (or a bare name)
 * @param {string} cssFamily the generic family `getTextContent` reports
 * @param {string} [sample] text set in this font — Bangla needs a Bangla family
 */
export function classifyPdfFont(font, cssFamily, sample) {
    const rawName = typeof font === 'string' ? font : font?.name;
    const name = String(rawName || '');
    const serifProbe = name.replace(/sans[-\s_]?serif/gi, '');
    const families = familyNamesOf(name);
    const isSerif = SERIF_NAMES.test(serifProbe);
    const isMono = MONO_NAMES.test(name);

    // The picker offers the whole catalogue, so the PDF's own family is often
    // simply *in* it — a document set in Roboto or Lato comes back in the same
    // font even when its embedded face cannot be reused.
    let fontFamily = catalogueFamilyFor(families);

    if (!fontFamily) {
        if (isMono) fontFamily = 'Courier New';
        else if (isSerif) fontFamily = 'Times New Roman';
        else {
            const css = String(cssFamily || '').toLowerCase().trim();
            fontFamily = css === 'monospace' ? 'Courier New'
                : css === 'serif' ? 'Times New Roman'
                    : 'Helvetica';
        }
    }

    // Latin families carry no Bangla glyphs at all, so a Bangla paragraph set in
    // one would be measured in that font and drawn in whatever the system
    // substitutes. Send it to a Bangla family instead.
    if (BENGALI.test(String(sample || ''))) {
        fontFamily = isSerif ? 'Noto Serif Bengali' : DEFAULT_FONT_FAMILY;
    }

    const object = typeof font === 'object' && font ? font : null;
    // pdf.js skips its own font machinery for Type 3 fonts (glyphs are content
    // streams, not a font program), so there is nothing to reuse.
    const loaded = object && !object.isType3Font
        ? (object.systemFontInfo?.css || (object.loadedName ? `"${object.loadedName}"` : ''))
        : '';

    return {
        fontFamily,
        bold: !!(object?.bold || object?.black)
            || /bold|black|heavy|semibold|[-,_]bd\b|[-,_](7|8|9)00\b/i.test(name),
        italic: !!object?.italic || /italic|oblique|[-,_]it\b/i.test(name),
        embedded: loaded
            ? {
                fontStack: [loaded, ...families.map((f) => `"${f}"`), fontCss(fontFamily)].join(', '),
                fontLabel: families[families.length - 1] || null,
                // `commonObjs` is document-wide, so this id finds the font
                // program again at export time, from any page.
                fontId: object.loadedName || null,
                // What pdf.js paints the face with — `black` counts as bold,
                // the editor has one weight above regular.
                bold: !!(object.bold || object.black),
                italic: !!object.italic,
            }
            : null,
    };
}

/**
 * Text runs of a page grouped into visual lines, in view space.
 *
 * Grouping matters for the "Edit text" tool: a PDF line is usually several
 * runs (kerning, style switches), and replacing one run at a time produces
 * ragged results. Runs are merged while they share a baseline and sit close
 * horizontally.
 *
 * @returns {Promise<Array<{ id: string, text: string, x: number, y: number, width: number, height: number, fontSize: number, items: object[] }>>}
 */
export async function readTextLines(pdf, pageNumber) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    const fontIds = new Set(content.items.map((i) => i.fontName).filter(Boolean));
    const fonts = readFontDescriptors(page, fontIds);

    const runs = [];
    for (const item of content.items) {
        if (!item.str || !item.str.trim()) continue;
        const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
        const fontSize = Math.hypot(tx[2], tx[3]) || Math.abs(tx[3]) || 10;
        const width = item.width * (Math.hypot(tx[0], tx[1]) / (Math.hypot(item.transform[0], item.transform[1]) || 1));
        const height = item.height ? item.height * (fontSize / (item.height || fontSize)) : fontSize;
        runs.push({
            str: item.str,
            x: tx[4],
            baseline: tx[5],
            width: Number.isFinite(width) && width > 0 ? width : item.str.length * fontSize * 0.5,
            height: Number.isFinite(height) && height > 0 ? height : fontSize,
            fontSize,
            fontName: item.fontName,
            style: classifyPdfFont(
                fonts.get(item.fontName),
                content.styles?.[item.fontName]?.fontFamily,
                item.str,
            ),
        });
    }

    runs.sort((a, b) => (a.baseline - b.baseline) || (a.x - b.x));

    /**
     * Left edges shared by several rows are a column (a contents page's page
     * numbers, a two-column layout), so a wide gap in front of one is a real
     * break. A wide gap in front of anything else is just loose word spacing or
     * a tab — merging it keeps rows like "Increment cycle:" in one piece
     * instead of splitting them into separate editable fragments.
     */
    const columnHits = new Map();
    for (const run of runs) {
        const key = Math.round(run.x / 3);
        columnHits.set(key, (columnHits.get(key) || 0) + 1);
    }
    const startsColumn = (x) => {
        const key = Math.round(x / 3);
        const near = (columnHits.get(key - 1) || 0) + (columnHits.get(key) || 0) + (columnHits.get(key + 1) || 0);
        return near >= 3;
    };

    const lines = [];
    for (const run of runs) {
        const last = lines[lines.length - 1];
        const gap = last ? run.x - (last.x + last.width) : Infinity;
        const sameLine = last
            && Math.abs(last.baseline - run.baseline) <= Math.max(1.5, last.fontSize * LINE_TOLERANCE)
            && run.x >= last.x - 2
            && (
                gap <= Math.max(12, last.fontSize * 1.6)
                || (gap < last.fontSize * 8 && !startsColumn(run.x))
            );
        if (sameLine) {
            last.text += gap > last.fontSize * 0.22 ? ` ${run.str}` : run.str;
            last.width = (run.x + run.width) - last.x;
            last.fontSize = Math.max(last.fontSize, run.fontSize);
            last.items.push(run);
        } else {
            lines.push({
                text: run.str,
                x: run.x,
                baseline: run.baseline,
                width: run.width,
                fontSize: run.fontSize,
                // The first run sets the line's style — mixed-style lines are
                // rare and the user can still change it in the options bar.
                style: run.style,
                items: [run],
            });
        }
    }

    return lines.map((line, i) => ({
        id: `p${pageNumber}-l${i}`,
        text: line.text,
        fontSize: line.fontSize,
        style: line.style || { fontFamily: 'Helvetica', bold: false, italic: false, embedded: null },
        x: line.x,
        y: line.baseline - line.fontSize * 0.82,
        width: Math.max(2, line.width),
        height: line.fontSize * 1.16,
        baseline: line.baseline,
        items: line.items,
    }));
}

const median = (values) => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
};

/**
 * Lines merged into paragraph blocks — the unit "Edit text" actually works on.
 *
 * Editing one line at a time is useless for prose: adding a word should push
 * the rest of the paragraph along, which can only happen if the whole paragraph
 * is one editable box. Lines join while they keep the same style, sit on the
 * paragraph's line pitch and share a left or right edge.
 *
 * @returns {Promise<Array<{ id, text, x, y, width, height, fontSize, lineHeight,
 *   align: 'left'|'justify', style: object, lines: object[] }>>}
 */
export async function readTextBlocks(pdf, pageNumber) {
    const lines = await readTextLines(pdf, pageNumber);
    if (!lines.length) return [];

    // The embedded face is part of the identity: two paragraphs set in
    // different document fonts can share a catalogue family and must not merge.
    const sameStyle = (a, b) => a.style?.fontFamily === b.style?.fontFamily
        && (a.style?.embedded?.fontStack || '') === (b.style?.embedded?.fontStack || '')
        && !!a.style?.bold === !!b.style?.bold
        && !!a.style?.italic === !!b.style?.italic;

    const blocks = [];
    let current = null;

    for (const line of lines) {
        const previous = current?.lines[current.lines.length - 1];
        const pitch = previous ? line.baseline - previous.baseline : 0;
        const sizeRatio = previous ? line.fontSize / previous.fontSize : 1;

        const continues = previous
            && sameStyle(previous, line)
            && sizeRatio > 0.85 && sizeRatio < 1.18
            // A gap larger than ~2 lines is a new paragraph, and a negative or
            // zero pitch means a new column or overlapping content.
            && pitch > line.fontSize * 0.6
            && pitch < line.fontSize * 2.1
            // Wrapped lines share an edge; an indented first line still shares
            // the right one, which is why either side counts.
            && (Math.abs(line.x - previous.x) < line.fontSize * 2.2
                || Math.abs((line.x + line.width) - (previous.x + previous.width)) < line.fontSize * 1.2);

        if (continues) {
            current.lines.push(line);
        } else {
            current = { lines: [line] };
            blocks.push(current);
        }
    }

    return blocks.map((block, i) => {
        const { lines: rows } = block;
        const x = Math.min(...rows.map((l) => l.x));
        const right = Math.max(...rows.map((l) => l.x + l.width));
        const fontSize = median(rows.map((l) => l.fontSize));
        const pitches = rows.slice(1).map((l, n) => l.baseline - rows[n].baseline);
        const pitch = pitches.length ? median(pitches) : fontSize * 1.2;
        const top = rows[0].y;
        const bottom = rows[rows.length - 1].y + rows[rows.length - 1].height;

        // Justified text has every line but the last ending on the same right
        // edge. Reproducing that keeps an edited paragraph looking untouched.
        const fullLines = rows.slice(0, -1);
        const rightEdges = fullLines.map((l) => l.x + l.width);
        const flush = rightEdges.length > 0
            && Math.max(...rightEdges) - Math.min(...rightEdges) < fontSize * 0.6;
        const lastShorter = rows.length > 1
            && (rows[rows.length - 1].x + rows[rows.length - 1].width) < right - fontSize * 0.6;

        return {
            id: `p${pageNumber}-b${i}`,
            text: rows.map((l) => l.text.trim()).join(' ').replace(/\s+/g, ' '),
            x,
            y: top,
            width: Math.max(4, right - x),
            height: Math.max(fontSize, bottom - top),
            fontSize,
            lineHeight: Math.max(1, pitch / Math.max(1, fontSize)),
            align: flush && lastShorter ? 'justify' : 'left',
            style: rows[0].style,
            lines: rows,
        };
    });
}

/** Case-insensitive matches for `query` on one page, as view-space rects. */
export async function searchPage(pdf, pageNumber, query) {
    if (!query) return [];
    const lines = await readTextLines(pdf, pageNumber);
    const needle = query.toLowerCase();
    const hits = [];

    for (const line of lines) {
        const haystack = line.text.toLowerCase();
        let from = 0;
        for (;;) {
            const at = haystack.indexOf(needle, from);
            if (at < 0) break;
            const charW = line.width / Math.max(1, line.text.length);
            hits.push({
                pageNumber,
                lineId: line.id,
                text: line.text,
                x: line.x + charW * at,
                y: line.y,
                width: Math.max(3, charW * needle.length),
                height: line.height,
            });
            from = at + needle.length;
        }
    }
    return hits;
}

const hex = (r, g, b) => `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;

/** Rects arrive as `{w,h}` from the object model and `{width,height}` from text lines. */
function readRegion(canvas, rect, scale, pad = 3) {
    const rw = rect.width ?? rect.w;
    const rh = rect.height ?? rect.h;
    if (!Number.isFinite(rw) || !Number.isFinite(rh) || !Number.isFinite(scale) || scale <= 0) return null;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const x = Math.max(0, Math.round((rect.x - pad) * scale));
    const y = Math.max(0, Math.round((rect.y - pad) * scale));
    const w = Math.min(canvas.width - x, Math.round((rw + pad * 2) * scale));
    const h = Math.min(canvas.height - y, Math.round((rh + pad * 2) * scale));
    if (!(w >= 2) || !(h >= 2)) return null;
    return ctx.getImageData(x, y, w, h).data;
}

/**
 * Most frequent colour among the pixels `accept` keeps.
 *
 * Colours are bucketed at 4 bits per channel to survive anti-aliasing, but the
 * value returned is the exact mean *inside* the winning bucket — quantising the
 * answer itself turns a pure-white page into a visible #f8f8f8 patch.
 */
function dominantColor(data, accept) {
    const sums = new Map();
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 40) continue;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        if (accept && !accept(r, g, b)) continue;
        const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
        const bucket = sums.get(key);
        if (bucket) {
            bucket[0] += r; bucket[1] += g; bucket[2] += b; bucket[3] += 1;
        } else {
            sums.set(key, [r, g, b, 1]);
        }
    }

    let best = null;
    for (const bucket of sums.values()) {
        if (!best || bucket[3] > best[3]) best = bucket;
    }
    if (!best || best[3] < 4) return null;
    return hex(best[0] / best[3], best[1] / best[3], best[2] / best[3]);
}

const luma = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b;

/**
 * Extent of the PAINTED ink inside a rendered region, in points relative to
 * that region's origin.
 *
 * "Edit text" sizes its cover from this rather than trusting pdf.js's reported
 * advance widths alone. A font whose `/Widths` disagree with the glyph program
 * it ships — or a run drawn with horizontal scaling — reports a paragraph
 * narrower than it prints, and a cover cut to that width leaves the tail of
 * every source line showing beside the replacement.
 *
 * Growth stops at a blank gutter wider than `maxGap` (or `maxRowGap` down the
 * page), so a page number, a marginal note or a second column beside the
 * paragraph is never swallowed into the box.
 *
 * @returns {{ left: number, right: number, top: number, bottom: number } | null}
 */
export function measureInkBounds(canvas, scale, { maxGap = 14, maxRowGap = 8, tolerance = 26 } = {}) {
    if (!canvas || !(scale > 0)) return null;
    const { width, height } = canvas;
    if (!(width >= 2) || !(height >= 2)) return null;

    let data;
    try {
        data = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, width, height).data;
    } catch {
        return null;
    }

    // The page colour is whatever most of the region is, so the bright end of
    // the histogram is the background and everything meaningfully darker is ink.
    const histogram = new Uint32Array(256);
    let counted = 0;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 40) continue;
        histogram[Math.min(255, Math.round(luma(data[i], data[i + 1], data[i + 2])))] += 1;
        counted += 1;
    }
    if (!counted) return null;
    let mode = 255;
    let modeCount = 0;
    for (let l = 0; l < 256; l += 1) {
        if (histogram[l] > modeCount) { modeCount = histogram[l]; mode = l; }
    }
    const cut = mode - tolerance;

    const colInk = new Uint8Array(width);
    const rowInk = new Uint8Array(height);
    for (let y = 0; y < height; y += 1) {
        const row = y * width * 4;
        for (let x = 0; x < width; x += 1) {
            const i = row + x * 4;
            if (data[i + 3] < 40) continue;
            if (luma(data[i], data[i + 1], data[i + 2]) < cut) {
                colInk[x] = 1;
                rowInk[y] = 1;
            }
        }
    }

    /** First and last set index, allowing runs to bridge gaps up to `gapPx`. */
    const span = (flags, gapPx) => {
        let first = -1;
        let last = -1;
        let blank = 0;
        for (let i = 0; i < flags.length; i += 1) {
            if (flags[i]) {
                if (first < 0) { first = i; last = i; blank = 0; continue; }
                if (blank > gapPx) break;
                last = i;
                blank = 0;
            } else if (first >= 0) {
                blank += 1;
            }
        }
        return first < 0 ? null : { first, last };
    };

    const cols = span(colInk, Math.max(2, maxGap * scale));
    const rows = span(rowInk, Math.max(2, maxRowGap * scale));
    if (!cols || !rows) return null;

    return {
        left: cols.first / scale,
        right: (cols.last + 1) / scale,
        top: rows.first / scale,
        bottom: (rows.last + 1) / scale,
    };
}

/** Dominant (background) colour of a rect in the rendered page. */
export function sampleBackgroundColor(canvas, rect, scale) {
    try {
        const data = readRegion(canvas, rect, scale);
        // A null result also covers the not-yet-rendered page, whose pixels are
        // transparent black — counting those paints the patch black.
        return (data && dominantColor(data)) || '#FFFFFF';
    } catch {
        return '#FFFFFF';
    }
}

/**
 * Ink colour of a text line.
 *
 * Glyph pixels are the dark minority in the rect, so this first keeps only the
 * darker end of the range and then takes the dominant colour of those — which
 * reads a coloured heading correctly instead of averaging it toward the page.
 */
export function sampleTextColor(canvas, rect, scale, fallback = '#1C1815') {
    try {
        const data = readRegion(canvas, rect, scale, 0);
        if (!data) return fallback;

        let min = Infinity;
        let max = -Infinity;
        for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] < 40) continue;
            const l = luma(data[i], data[i + 1], data[i + 2]);
            if (l < min) min = l;
            if (l > max) max = l;
        }
        if (!Number.isFinite(min) || max - min < 12) return fallback;

        // Average only the very darkest pixels — the stroke cores. At 100% zoom
        // a 9pt glyph is mostly anti-aliased edge, so anything broader (a mean,
        // or the most common dark colour) reads back as mid-grey and the
        // replacement looks washed out beside the text it replaced. Taking the
        // extreme keeps a coloured heading's hue too, since its darkest pixels
        // are still that colour.
        const pixels = [];
        for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] < 40) continue;
            pixels.push([luma(data[i], data[i + 1], data[i + 2]), data[i], data[i + 1], data[i + 2]]);
        }
        if (pixels.length < 8) return fallback;

        pixels.sort((a, b) => a[0] - b[0]);
        const take = Math.max(3, Math.round(pixels.length * 0.01));
        let r = 0; let g = 0; let b = 0;
        for (let i = 0; i < take; i += 1) {
            r += pixels[i][1];
            g += pixels[i][2];
            b += pixels[i][3];
        }
        r /= take; g /= take; b /= take;

        // Snap the last few levels of anti-aliasing off pure black ink.
        if (Math.max(r, g, b) <= 40 && Math.max(r, g, b) - Math.min(r, g, b) <= 16) return '#000000';
        return hex(r, g, b);
    } catch {
        return fallback;
    }
}
